import type { ChangeEvent, FormEvent } from 'react';
import { Mail } from 'lucide-react';
import type { State } from '@/components/control/types';
import type { Data, ValidationField } from '@/redux/user-form/user-form.types';
import type { FormLayoutCells, FormLayoutRow } from '@/types';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import Checkbox from '@/components/checkbox';
import Control from '@/components/control';
import FieldError from '@/components/field-error';
import Input from '@/components/input';
import Radio from '@/components/radio';
import SuspiciousFields from '@/components/suspicious-fields';
import { useContext } from '@/components/slider-type/context';
import { useAppDispatch, useAppSelector } from '@/redux/store';
import { ConfigSlice } from '@/redux/config';
import { UserFormSlice } from '@/redux/user-form';
import { cells, labels, validate } from '@/redux/user-form/user-form.utils';
import Condition from '@/utils/condition';
import Converter from '@/utils/converter';
import FormSchema from '@/utils/form-schema';
import Phone from '@/utils/phone';
import Track from '@/utils/track';
import '@/components/slider-type/components/user-form/user-form-v1.css';
import '@/components/slider-type/components/user-form/user-form-v2.css';

/**
 * Character filter per field key, applied while typing and pasting.
 */
const FILTERS: Data<(value: string) => string> = {
  phone: Phone.sanitize,
};

/**
 * Value the confirmation dialog opens with, when it differs from what the user
 * typed - a phone number arrives there corrected and in E.164.
 */
const SUGGESTIONS: Data<(value: string) => string> = {
  phone: Phone.suggest,
};

export default function UserForm() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { onSubmit } = useContext();
  const cssVersion = useAppSelector(ConfigSlice.selectors.cssVersion);
  const { schema, layout, button } = useAppSelector(ConfigSlice.selectors.signForm);
  const withEnvelopeIcon = useAppSelector(ConfigSlice.selectors.signWithEnvelopIcon);
  const withEmojies = useAppSelector(ConfigSlice.selectors.signWithEmojies);
  const form = useAppSelector(UserFormSlice.selectors.form);
  const touch = useAppSelector(UserFormSlice.selectors.touch);
  const validation = useAppSelector(UserFormSlice.selectors.validation);
  const isDirty = useAppSelector(UserFormSlice.selectors.isDirty);
  const isInitialized = useAppSelector(UserFormSlice.selectors.isInitialized);
  const errors = useAppSelector(ConfigSlice.selectors.signFormErrors);
  const [visibleKeys, setVisibleKeys] = useState<string[]>([]);
  const [suspiciousOn, setSuspiciousOn] = useState(false);
  const [draft, setDraft] = useState<Data<string>>({});

  const removeHiddenRow = (row: FormLayoutRow): boolean => !row.hidden?.call(null);

  const removeHiddenCell = (row: FormLayoutCells): boolean => !row.hidden?.call(null);

  const updateForm = (key: string, value: string) => {
    if (form[key] === value) return;
    dispatch(
      UserFormSlice.actions.setUserFormKey({
        layout,
        schema: schema(visibleKeys),
        data: { key, value },
      }),
    );
  };

  const updateTouch = (key: string) => {
    if (touch[key]) return;
    dispatch(UserFormSlice.actions.setTouchKey({ layout, schema: schema(visibleKeys), data: key }));
  };

  const controlState = (state: ValidationField['state']): State => (state === 'suspicious' ? 'idle' : state);

  const getCell = (cell: FormLayoutCells) => {
    const valid = validation.fields[cell.key].state === 'success';
    const invalid = validation.fields[cell.key].state === 'error';
    const state = controlState(validation.fields[cell.key].state);

    const getLabel = (label: string) => {
      if (!withEmojies) return label;
      if (['first_name', 'last_name'].includes(cell.key)) return '👋 ' + label;
      if (['email'].includes(cell.key)) return '✉️ ' + label;
      if (['phone', 'tel'].includes(cell.key)) return '📞 ' + label;
      return label;
    };

    switch (cell.type) {
      case 'checkbox':
        return cssVersion === 1 ? (
          <Checkbox
            value={form[cell.key] === 'true'}
            label={cell.label}
            invalid={invalid}
            name={cell.key}
            onChange={(value, key) => updateForm(key, value.toString())}
            onBlur={() => updateTouch(cell.key)}
          />
        ) : (
          <Control.Checkbox
            state={state}
            id={cell.key}
            value={Converter.BooleanString.ToBoolean(form[cell.key])}
            disabled={false}
            onChange={(value, id) => updateForm(id, Converter.Boolean.ToBooleanString(value))}
            onBlur={(id) => updateTouch(id)}
          >
            {typeof cell.label === 'string' ? cell.label : cell.label()}
          </Control.Checkbox>
        );
      case 'radio':
        return cssVersion === 1 ? (
          <Radio
            isInvalid={invalid}
            value={form[cell.key]}
            name={cell.key}
            list={cell.choices}
            onChange={(key, value) => updateForm(key, value)}
            onBlur={() => updateTouch(cell.key)}
          />
        ) : (
          <Control.Radio
            state={state}
            orientation='horizontal'
            choices={cell.choices}
            id={cell.key}
            value={form[cell.key]}
            disabled={false}
            onChange={(value, id) => updateForm(id, value)}
            onBlur={(id) => updateTouch(id)}
          />
        );
      case 'text':
        return cssVersion === 1 ? (
          <Input
            isSuccessful={valid}
            isUnsuccessful={invalid}
            type='text'
            filter={FILTERS[cell.key]}
            label={cell.label}
            value={form[cell.key]}
            placeholder={cell.placeholder}
            name={cell.key}
            onChange={(e: ChangeEvent<HTMLInputElement>) => updateForm(cell.key, e.target.value)}
            onBlur={() => updateTouch(cell.key)}
          />
        ) : (
          <Control.Text
            state={state}
            filter={FILTERS[cell.key]}
            label={getLabel(cell.label)}
            id={cell.key}
            value={form[cell.key]}
            onChange={(value, id) => updateForm(id, value)}
            onBlur={(id) => updateTouch(id)}
          />
        );
    }
  };

  const draftValidation = useMemo(() => {
    if (!suspiciousOn) return null;
    return validate({
      schema: schema(visibleKeys),
      layout,
      form: { ...form, ...draft },
      touch: { ...touch, ...Object.fromEntries(Object.keys(draft).map((key) => [key, true])) },
      isDirty,
    });
  }, [suspiciousOn, draft, form, touch, isDirty, layout, visibleKeys, schema]);

  const suspiciousItems = useMemo(() => {
    if (!draftValidation) return [];
    const label = labels({ layout });
    const cell = cells({ layout });
    return Object.keys(draft).map((key) => {
      const field = draftValidation.fields[key];
      return {
        key,
        label: label[key],
        editable: cell[key]?.type === 'text',
        value: draft[key],
        state: controlState(field.state),
        invalid: field.state === 'error',
        filter: FILTERS[key],
        reason: FormSchema.message(field.issues[0], errors),
      };
    });
  }, [draftValidation, draft, layout, errors]);

  const openSuspicious = () => {
    setDraft(
      Object.fromEntries(
        validation.suspicious.map((key) => {
          const suggest = SUGGESTIONS[key];
          return [key, suggest ? suggest(form[key]) : form[key]];
        }),
      ),
    );
    setSuspiciousOn(true);
  };

  const onSuspiciousConfirm = () => {
    setSuspiciousOn(false);
    if (!draftValidation || draftValidation.hasErrors) {
      // Keep the edits so the user sees them next to the errors in the form.
      dispatch(UserFormSlice.actions.setUserFormValues({ layout, schema: schema(visibleKeys), data: draft }));
      Track.DataLayer.SubmitFormFailure();
      Track.Experiments.FormularSubmitFail();
      return;
    }
    Track.DataLayer.SubmitFormSuccess();
    Track.Experiments.FormularSubmit();
    dispatch(UserFormSlice.actions.setUserFormKeys(draftValidation.output));
    onSubmit(draftValidation.output);
  };

  const onSubmitForm = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!isDirty) dispatch(UserFormSlice.actions.setIsDirty({ layout, schema: schema(visibleKeys) }));
    if (validation.hasErrors) {
      Track.DataLayer.SubmitFormFailure();
      Track.Experiments.FormularSubmitFail();
      return;
    }
    if (validation.suspicious.length > 0) {
      openSuspicious();
      return;
    }
    Track.DataLayer.SubmitFormSuccess();
    Track.Experiments.FormularSubmit();
    dispatch(UserFormSlice.actions.setUserFormKeys(validation.output));
    onSubmit(validation.output);
  };

  useEffect(() => {
    dispatch(UserFormSlice.actions.setInitialize({ layout, schema: schema(visibleKeys) }));
    setVisibleKeys(
      layout
        .filter(removeHiddenRow)
        .map((row) => row.cells)
        .reduce((cells, row) => [...cells, ...row], [])
        .filter(removeHiddenCell)
        .map((cell) => cell.key),
    );
  }, [layout]);

  if (!isInitialized) return null;

  return (
    <form data-css-version={cssVersion} className='user-form' onSubmit={onSubmitForm}>
      <div className='user-form__main'>
        {layout.filter(removeHiddenRow).map((row, i, rows) => (
          <div key={i} className='user-form__row'>
            <div className='user-form__label'>{row.label}</div>
            <div className='user-form__cells'>
              {row.cells.filter(removeHiddenCell).map((cell) => (
                <div
                  key={cell.key}
                  className={clsx('user-form__cell', {
                    'user-form__cell--last-checkbox': rows.length - 1 === i && cell.type === 'checkbox',
                  })}
                >
                  {getCell(cell)}
                  <FieldError
                    on={validation.fields[cell.key].state === 'error'}
                    issues={validation.fields[cell.key].issues}
                  />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      {cssVersion === 1 ? (
        <button type='submit' className='user-form__button'>
          {t(Condition.getFirst(button, 'Einspruch kostenlos prüfen'))}
        </button>
      ) : (
        // TODO: remember to either delete or reinclude
        // <Control.Button type='submit' size='lg' color='next' info='100% kostenlos' fullWidth>
        //   {t(Condition.getFirst(button, 'Einspruch kostenlos prüfen'))}
        // </Control.Button>
        <Control.Button type='submit' size='lg' color='next' fullWidth>
          {withEnvelopeIcon && <Mail />}
          {t(Condition.getFirst(button, 'Einspruch kostenlos prüfen'))}
        </Control.Button>
      )}
      <SuspiciousFields
        on={suspiciousOn}
        items={suspiciousItems}
        onChange={(key, value) => setDraft((current) => ({ ...current, [key]: value }))}
        onConfirm={onSuspiciousConfirm}
        onCancel={() => setSuspiciousOn(false)}
      />
    </form>
  );
}
